import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiTruck, FiX, FiSave, FiDollarSign, FiSearch, FiPercent, FiUpload, FiDownload, FiPrinter, FiMoreVertical, FiBookOpen, FiCheckCircle } from 'react-icons/fi';
import { downloadVendorTemplate, importVendorsFromRows, readExcelFile } from '../../utils/partyImportHelper';
import { printVendors } from '../../utils/masterPrintHelper';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, where, writeBatch } from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { invalidateMaster, upsertLocalMaster, getItemsCached } from '../../utils/masterDataStore';
import ItemSelect from '../../components/ItemSelect';
import { isVendorPricingOn, VENDOR_PRICING_COLLECTION, vendorPricingDocId } from '../../utils/vendorPricingHelper';
import { getBaseUnit } from '../../utils/unitConversion';
import { isValidEmail, isValidPhone, isValidGST, writeAuditLog } from '../../utils/validationHelpers';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { createLinkedAccount, ensureParentAccountExists, deleteLinkedAccount } from '../../utils/coaInitializer';
// Balance column removed 2026-07-16 (Firebase cost): loadVendorBalances scanned
// the full-FY subTransactions on every page open. Balances live in the
// Vendor Balances / AP Aging reports.
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import { MasterHeaderActions, MasterAddButton, ContentCard, StatsRow, StatCard, StatIcon, StatInfo, StatValue, StatLabel } from '../../components/SharedStyles';
import useEscapeClose from '../../hooks/useEscapeClose';
import useTaxTerms from '../../hooks/useTaxTerms';

const Vendors = () => {
  const { userProfile, activeCompany, currentUser, activeBranch, activeFinancialYear, isModuleEnabled } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'vendors');
  // Vendor-wise Items & Pricing (module purchases_vendor_pricing, default off).
  const vendorPricingOn = isVendorPricingOn(isModuleEnabled);
  const [modalTab, setModalTab] = useState('profile'); // 'profile' | 'pricing'
  const [pricingRows, setPricingRows] = useState([]);   // [{ key, itemId, itemCode, itemName, price }]
  const [pricingOriginal, setPricingOriginal] = useState({}); // itemId → saved price
  const [pricingItems, setPricingItems] = useState([]);
  const [pricingSearch, setPricingSearch] = useState('');
  const { taxLabel, registrationLabel, registrationLabelFull } = useTaxTerms();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  // Live balances keyed by vendorId, computed from subTransactions so the
  // Balance column always matches Reports → Vendor Balances.
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingVendor, setEditingVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    vendorName: '',
    vendorType: 'Supplier',
    contactPerson: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    country: 'Pakistan',
    ntn: '',
    gstNo: '',
    paymentTerms: 30,
    bankName: '',
    bankAccountNo: '',
    bankBranch: '',
    whtApplicable: false,
    whtRate: 4.5,
    isActive: true,
    notes: ''
  });

  // Excel import
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Actions menu (portal-based)
  const navigate = useNavigate();
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const closeActionMenu = () => setOpenActionMenuId(null);
  const toggleActionMenu = (vendorId, e) => {
    if (openActionMenuId === vendorId) { closeActionMenu(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 200;
    const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
    const top = rect.bottom + 4;
    setActionMenuPos({ top, left: Math.max(8, left) });
    setOpenActionMenuId(vendorId);
  };
  useEffect(() => {
    if (!openActionMenuId) return;
    const close = (e) => {
      if (!e.target.closest('[data-action-menu]') && !e.target.closest('[data-action-trigger]')) closeActionMenu();
    };
    const onScroll = () => closeActionMenu();
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openActionMenuId]);

  const handleOpenLedger = (vendor) => {
    setOpenActionMenuId(null);
    if (!vendor.linkedAccountId) {
      alert('No linked account for this vendor. Please create one first.');
      return;
    }
    navigate('/reports/ledger', { state: { filterAccountId: vendor.linkedAccountId } });
  };

  const handleOpenPayment = (vendor) => {
    setOpenActionMenuId(null);
    navigate('/payments/cash-payment', {
      state: {
        prefill: {
          partyId: vendor.id,
          partyName: vendor.vendorName,
          linkedAccountId: vendor.linkedAccountId || null
        }
      }
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!canEdit) { alert('You do not have permission to import.'); return; }
    setImporting(true);
    setImportResults(null);
    setShowImportModal(true);
    try {
      const rows = await readExcelFile(file);
      if (rows.length === 0) {
        setImportResults({ success: 0, skipped: 0, failed: 0, errors: ['No rows found in the file.'] });
        setImporting(false);
        return;
      }
      const results = await importVendorsFromRows(
        activeCompany?.id, rows, userProfile?.uid,
        (p) => setImportProgress(p)
      );
      setImportResults(results);
      loadVendors();
    } catch (err) {
      console.error(err);
      setImportResults({ success: 0, skipped: 0, failed: 0, errors: ['Import failed: ' + err.message] });
    } finally {
      setImporting(false);
    }
  };

  // Keyboard shortcuts: Ctrl+N to open add modal, Ctrl+S to submit form
  useDocumentShortcuts({
    onNew: () => { resetForm(); setShowModal(true); },
    onSave: () => { const form = document.querySelector('form'); if (form) form.requestSubmit(); },
    viewMode: showModal ? 'form' : 'list',
    saving
  });

  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);
      const vendorsRef = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(vendorsRef, orderBy('vendorCode', 'asc'));
      const snapshot = await getDocs(q);

      const vendorsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setVendors(vendorsList);
      setFilteredVendors(vendorsList);
    } catch (error) {
      console.error('Error loading vendors:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredVendors(vendors);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredVendors(vendors.filter(vendor =>
        vendor.vendorName?.toLowerCase().includes(query) ||
        vendor.vendorCode?.toLowerCase().includes(query) ||
        vendor.phone?.includes(query) ||
        vendor.city?.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, vendors]);

  // Items for the price list picker — from the device cache (no reads in the usual case).
  useEffect(() => {
    if (!showModal || !vendorPricingOn || !activeCompany?.id || pricingItems.length) return;
    getItemsCached(activeCompany.id)
      .then(list => setPricingItems((list || [])
        .filter(i => i.isDeleted !== true)
        .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''))))
      .catch(err => console.error('Failed to load items for vendor pricing:', err));
  }, [showModal, vendorPricingOn, activeCompany?.id, pricingItems.length]);

  const loadPricingForVendor = async (vendorId) => {
    setPricingRows([]);
    setPricingOriginal({});
    if (!vendorPricingOn || !vendorId || !activeCompany?.id) return;
    try {
      const snap = await getDocs(query(collection(db, 'companies', activeCompany.id, VENDOR_PRICING_COLLECTION), where('vendorId', '==', vendorId)));
      const rows = snap.docs.map(d => {
        const r = d.data();
        return { key: d.id, itemId: r.itemId || '', itemCode: r.itemCode || '', itemName: r.itemName || '', price: Number(r.price) || '' };
      }).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''));
      const orig = {};
      rows.forEach(r => { if (r.itemId) orig[r.itemId] = Number(r.price) || 0; });
      setPricingRows(rows);
      setPricingOriginal(orig);
    } catch (err) {
      console.error('Failed to load vendor pricing:', err);
    }
  };

  // Save the price list: writes only new / changed rows and deletes removed ones.
  const saveVendorPricing = async (vendorId) => {
    if (!vendorPricingOn || !vendorId) return;
    const colRef = collection(db, 'companies', activeCompany.id, VENDOR_PRICING_COLLECTION);
    const now = new Date().toISOString();
    const ops = [];
    const kept = new Set();
    pricingRows.forEach(r => {
      if (!r.itemId) return;
      kept.add(r.itemId);
      const price = Math.round((Number(r.price) || 0) * 10000) / 10000;
      const isNew = pricingOriginal[r.itemId] === undefined;
      if (!isNew && pricingOriginal[r.itemId] === price) return;
      ops.push(['set', doc(colRef, vendorPricingDocId(vendorId, r.itemId)), {
        vendorId, itemId: r.itemId, itemCode: r.itemCode || '', itemName: r.itemName || '', price, updatedAt: now,
        ...(isNew ? { createdAt: now, createdBy: currentUser?.uid || null } : {}),
      }]);
    });
    Object.keys(pricingOriginal).forEach(itemId => {
      if (!kept.has(itemId)) ops.push(['delete', doc(colRef, vendorPricingDocId(vendorId, itemId))]);
    });
    for (let i = 0; i < ops.length; i += 400) {
      const batch = writeBatch(db);
      ops.slice(i, i + 400).forEach(([kind, ref, data]) => (kind === 'set' ? batch.set(ref, data, { merge: true }) : batch.delete(ref)));
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
    }
  };

  const generateVendorCode = async () => {
    const vendorsRef = collection(db, 'companies', activeCompany.id, 'vendors');
    const snapshot = await getDocs(vendorsRef);
    const count = snapshot.size + 1;
    return `VEND-${String(count).padStart(4, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.vendorName) {
      alert('Please enter vendor name');
      return;
    }
    if (formData.email && !isValidEmail(formData.email)) {
      alert('Please enter a valid email address'); return;
    }
    if (formData.phone && !isValidPhone(formData.phone)) {
      alert('Please enter a valid phone number'); return;
    }
    if (formData.gstNo && !isValidGST(formData.gstNo)) {
      alert('Please enter a valid GST number'); return;
    }
    if (vendorPricingOn) {
      const seen = new Set();
      for (const r of pricingRows) {
        if (!r.itemId) continue; // empty rows are ignored
        if (seen.has(r.itemId)) { setModalTab('pricing'); alert(`"${r.itemName || r.itemCode}" is listed more than once in Custom Items & Pricing.`); return; }
        seen.add(r.itemId);
        if (!(Number(r.price) > 0)) { setModalTab('pricing'); alert(`Enter a price for "${r.itemName || r.itemCode}" in Custom Items & Pricing.`); return; }
      }
    }

    setSaving(true);
    try {
      // Captured on both branches for the local-cache write below.
      let savedVendorId = editingVendor?.id || null;
      let savedVendorRow = null;

      if (editingVendor) {
        // Update existing vendor
        const vendorRef = doc(db, 'companies', activeCompany.id, 'vendors', editingVendor.id);
        savedVendorRow = {
          ...formData,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser.uid
        };
        await updateDoc(vendorRef, savedVendorRow);

        // If vendor has linked account, update name if changed
        if (editingVendor.linkedAccountId && editingVendor.vendorName !== formData.vendorName) {
          try {
            const linkedAccountRef = doc(db, 'companies', activeCompany.id, 'accounts', editingVendor.linkedAccountId);
            await updateDoc(linkedAccountRef, {
              name: `Vendor: ${formData.vendorName}`,
              updatedAt: new Date().toISOString()
            });
          } catch (accountError) {
            console.error('Error updating linked account:', accountError);
          }
        }
      } else {
        // First validate parent account exists (this will auto-repair COA if needed)
        const parentCheck = await ensureParentAccountExists(activeCompany.id, 'vendor', currentUser.uid);

        if (!parentCheck.success) {
          alert(`Cannot create vendor: ${parentCheck.message}. Please ensure Chart of Accounts is properly initialized.`);
          setSaving(false);
          return;
        }

        // Create new vendor
        const vendorsRef = collection(db, 'companies', activeCompany.id, 'vendors');
        const vendorCode = await generateVendorCode();

        // Create vendor document
        savedVendorRow = {
          vendorCode,
          ...formData,
          currentBalance: 0,
          createdAt: new Date().toISOString(),
          createdBy: currentUser.uid
        };
        const vendorDoc = await addDoc(vendorsRef, savedVendorRow);
        savedVendorId = vendorDoc.id;

        // Create linked account using new simplified API
        try {
          const accountResult = await createLinkedAccount(
            activeCompany.id,
            'vendor',
            vendorDoc.id,
            formData.vendorName,
            currentUser.uid
          );

          if (accountResult.success) {
            // Update vendor with linked account ID and code
            await updateDoc(doc(vendorsRef, vendorDoc.id), {
              linkedAccountId: accountResult.accountId,
              linkedAccountCode: accountResult.accountCode
            });
          } else {
            console.warn('Failed to create linked account:', accountResult.message);
            alert('Vendor created but failed to create linked account. You can create it manually later.');
          }
        } catch (accountError) {
          console.error('Error creating linked account:', accountError);
          alert('Vendor created but failed to create linked account. You may need to create it manually.');
        }
      }

      // Put the saved vendor into the device's local cache NOW so it can be
      // picked on a purchase invoice immediately — invalidateMaster() below is
      // fire-and-forget and re-downloads the whole vendor list, which a user
      // navigating straight to a form will beat.
      if (savedVendorId && savedVendorRow) {
        await upsertLocalMaster(activeCompany?.id, 'vendors', { id: savedVendorId, ...savedVendorRow });
      }

      if (vendorPricingOn && savedVendorId) {
        try {
          await saveVendorPricing(savedVendorId);
        } catch (pricingErr) {
          console.error('Error saving vendor pricing:', pricingErr);
          alert('Vendor saved, but its Custom Items & Pricing list could not be saved. Please open the vendor and save again.');
        }
      }

      invalidateMaster(activeCompany?.id, 'vendors');
      invalidateMaster(activeCompany?.id, 'accounts');
      setShowModal(false);
      resetForm();
      await loadVendors();
    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Are you sure you want to delete "${vendor.vendorName}"?`)) {
      return;
    }

    try {
      const { hasReferences, message } = await checkEntityReferences(activeCompany.id, 'vendor', vendor.id);
      if (hasReferences) {
        alert(message);
        return;
      }

      const vendorRef = doc(db, 'companies', activeCompany.id, 'vendors', vendor.id);
      await deleteDoc(vendorRef);
      await deleteLinkedAccount(activeCompany.id, 'vendor', vendor.id);
      if (vendorPricingOn) {
        try {
          const vp = await getDocs(query(collection(db, 'companies', activeCompany.id, VENDOR_PRICING_COLLECTION), where('vendorId', '==', vendor.id)));
          if (!vp.empty) { const b = writeBatch(db); vp.docs.forEach(d => b.delete(d.ref)); await b.commit(); }
        } catch (cleanupErr) {
          console.warn('Failed to clean up vendor pricing rows on delete:', cleanupErr);
        }
      }
      await writeAuditLog(activeCompany?.id, null, 'DELETE', 'vendor', vendor.id, `Deleted vendor: ${vendor.vendorName || vendor.companyName}`);
      invalidateMaster(activeCompany?.id, 'vendors');
      invalidateMaster(activeCompany?.id, 'accounts');
      await loadVendors();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      alert('Failed to delete vendor');
    }
  };

  const resetForm = () => {
    setFormData({
      vendorName: '',
      vendorType: 'Supplier',
      contactPerson: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      city: '',
      country: 'Pakistan',
      ntn: '',
      gstNo: '',
      paymentTerms: 30,
      bankName: '',
      bankAccountNo: '',
      bankBranch: '',
      whtApplicable: false,
      whtRate: 4.5,
      isActive: true,
      notes: ''
    });
    setEditingVendor(null);
    setModalTab('profile');
    setPricingRows([]);
    setPricingOriginal({});
    setPricingSearch('');
  };

  const openEditModal = (vendor) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingVendor(vendor);
    setFormData({
      vendorName: vendor.vendorName || '',
      vendorType: vendor.vendorType || 'Supplier',
      contactPerson: vendor.contactPerson || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      mobile: vendor.mobile || '',
      address: vendor.address || '',
      city: vendor.city || '',
      country: vendor.country || 'Pakistan',
      ntn: vendor.ntn || '',
      gstNo: vendor.gstNo || '',
      paymentTerms: vendor.paymentTerms || 30,
      bankName: vendor.bankName || '',
      bankAccountNo: vendor.bankAccountNo || '',
      bankBranch: vendor.bankBranch || '',
      whtApplicable: vendor.whtApplicable || false,
      whtRate: vendor.whtRate || 4.5,
      isActive: vendor.isActive !== false,
      notes: vendor.notes || ''
    });
    setModalTab('profile');
    setPricingSearch('');
    loadPricingForVendor(vendor.id);
    setShowModal(true);
  };

  return (
    <PageLayout pageTitle="Vendors" breadcrumb="Master Data / Vendors">
      <MasterHeaderActions style={{ gap: 10, display: 'flex', flexWrap: 'wrap' }}>
        <ImportBtn
          onClick={() => printVendors(filteredVendors, { activeCompany, filterLabel: searchQuery ? `Search: "${searchQuery}"` : '' })}
          title="Print vendor list"
        >
          <FiPrinter /> Print
        </ImportBtn>
        <ImportBtn onClick={downloadVendorTemplate} title="Download Excel template">
          <FiDownload /> Download Template
        </ImportBtn>
        {canEdit && (
          <ImportBtn onClick={() => fileInputRef.current?.click()} title="Import vendors from Excel">
            <FiUpload /> Import Excel
          </ImportBtn>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        {canEdit && (
          <MasterAddButton onClick={() => { resetForm(); setShowModal(true); }}>
            <FiPlus /> Add Vendor
          </MasterAddButton>
        )}
      </MasterHeaderActions>

      {(() => {
        const totalVendors = vendors.length;
        const activeVendors = vendors.filter(v => v.isActive !== false).length;
        return (
          <StatsRow>
            <StatCard>
              <StatIcon $color={colors.primary.main}><FiTruck /></StatIcon>
              <StatInfo>
                <StatValue>{totalVendors}</StatValue>
                <StatLabel>Total Vendors</StatLabel>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $color={colors.success.main}><FiCheckCircle /></StatIcon>
              <StatInfo>
                <StatValue>{activeVendors}</StatValue>
                <StatLabel>Active Vendors</StatLabel>
              </StatInfo>
            </StatCard>
          </StatsRow>
        );
      })()}

      {/* Actions menu (portal — escapes table overflow) */}
      {openActionMenuId && (() => {
        const vendor = vendors.find(v => v.id === openActionMenuId);
        if (!vendor) return null;
        return createPortal(
          <ActionDropdown
            data-action-menu
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
          >
            {canChange && (
              <ActionItem onClick={() => { closeActionMenu(); openEditModal(vendor); }}>
                <FiEdit2 size={14} /> Edit
              </ActionItem>
            )}
            <ActionItem onClick={() => handleOpenLedger(vendor)}>
              <FiBookOpen size={14} /> View Ledger
            </ActionItem>
            <ActionItem onClick={() => handleOpenPayment(vendor)}>
              <FiDollarSign size={14} /> Make Payment
            </ActionItem>
            {canDelete && (
              <>
                <ActionDivider />
                <ActionItem $danger onClick={() => { closeActionMenu(); handleDelete(vendor); }}>
                  <FiTrash2 size={14} /> Delete
                </ActionItem>
              </>
            )}
          </ActionDropdown>,
          document.body
        );
      })()}

      {showImportModal && (
        <ImportOverlay onClick={() => !importing && setShowImportModal(false)}>
          <ImportModalBox onClick={(e) => e.stopPropagation()}>
            <ImportHeader>
              <h3>Vendor Import</h3>
              {!importing && (
                <button onClick={() => setShowImportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <FiX />
                </button>
              )}
            </ImportHeader>
            <ImportBody>
              {importing ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div style={{ fontSize: 14, marginBottom: 12 }}>
                    Importing… {importProgress.current} / {importProgress.total}
                  </div>
                  <ProgressBarOuter>
                    <ProgressBarFill style={{ width: `${importProgress.total ? (importProgress.current / importProgress.total) * 100 : 0}%` }} />
                  </ProgressBarOuter>
                </div>
              ) : importResults && (
                <div>
                  <ResultRow $color={colors.success.main}><strong>{importResults.success}</strong> imported</ResultRow>
                  <ResultRow $color={colors.warning.main}><strong>{importResults.skipped}</strong> skipped (already exists)</ResultRow>
                  <ResultRow $color={colors.error.main}><strong>{importResults.failed}</strong> failed</ResultRow>
                  {importResults.errors.length > 0 && (
                    <ErrorList>
                      <strong style={{ fontSize: 12, color: colors.text.secondary }}>Issues:</strong>
                      {importResults.errors.slice(0, 10).map((err, i) => (
                        <ErrorItem key={i}>{err}</ErrorItem>
                      ))}
                      {importResults.errors.length > 10 && (
                        <ErrorItem>...and {importResults.errors.length - 10} more</ErrorItem>
                      )}
                    </ErrorList>
                  )}
                </div>
              )}
            </ImportBody>
            {!importing && (
              <ImportFooter>
                <MasterAddButton onClick={() => setShowImportModal(false)}>Close</MasterAddButton>
              </ImportFooter>
            )}
          </ImportModalBox>
        </ImportOverlay>
      )}

      <ContentCard>
        <SearchBar>
          <SearchInput>
            <FiSearch />
            <input
              type="text"
              placeholder="Search vendors by name, code, phone, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchInput>
        </SearchBar>

        {loading ? (
          <LoadingState>Loading vendors...</LoadingState>
        ) : filteredVendors.length === 0 ? (
          <EmptyState>
            <FiTruck />
            <p>{searchQuery ? 'No vendors found matching your search' : 'No vendors added yet'}</p>
            <small>Click "Add Vendor" to create your first vendor</small>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Vendor Name</Th>
                <Th>Type</Th>
                <Th>Phone</Th>
                <Th>City</Th>
                <Th>{registrationLabel}</Th>
                <Th>WHT</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map(vendor => (
                <Tr key={vendor.id}>
                  <Td><strong>{vendor.vendorCode}</strong></Td>
                  <Td>
                    <VendorInfo>
                      <VendorName>{vendor.vendorName}</VendorName>
                      {vendor.contactPerson && <ContactPerson>{vendor.contactPerson}</ContactPerson>}
                    </VendorInfo>
                  </Td>
                  <Td>
                    <TypeBadge $type={vendor.vendorType}>
                      {vendor.vendorType}
                    </TypeBadge>
                  </Td>
                  <Td>{vendor.phone}</Td>
                  <Td>{vendor.city}</Td>
                  <Td>{vendor.ntn || '-'}</Td>
                  <Td>
                    {vendor.whtApplicable ? (
                      <WhtBadge>{vendor.whtRate}%</WhtBadge>
                    ) : (
                      <span style={{ color: colors.text.tertiary }}>-</span>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge $active={vendor.isActive}>
                      {vendor.isActive ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <IconButton
                      data-action-trigger
                      onClick={(e) => toggleActionMenu(vendor.id, e)}
                      title="Actions"
                    >
                      <FiMoreVertical />
                    </IconButton>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </ContentCard>

      {/* Modal */}
      {showModal && (
        <ModalOverlay>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FiTruck />
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}><FiX /></CloseButton>
            </ModalHeader>

            <ModalBody>
              <form onSubmit={handleSubmit} onInvalidCapture={vendorPricingOn ? () => setModalTab('profile') : undefined}>
                {vendorPricingOn && (
                  <VpTabStrip>
                    <VpTabBtn type="button" $active={modalTab === 'profile'} onClick={() => setModalTab('profile')}>Profile</VpTabBtn>
                    <VpTabBtn type="button" data-vp-tab $active={modalTab === 'pricing'} onClick={() => setModalTab('pricing')}>
                      Custom Items & Pricing
                      {pricingRows.filter(r => r.itemId).length > 0 && <VpCount>{pricingRows.filter(r => r.itemId).length}</VpCount>}
                    </VpTabBtn>
                  </VpTabStrip>
                )}
                <div style={{ display: modalTab === 'profile' ? 'block' : 'none' }}>
                <SectionTitle>Basic Information</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Vendor Name *</Label>
                    <Input
                      value={formData.vendorName}
                      onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                      placeholder="Business/Company name"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Vendor Type *</Label>
                    <Select
                      value={formData.vendorType}
                      onChange={(e) => setFormData({ ...formData, vendorType: e.target.value })}
                    >
                      <option value="Supplier">Supplier</option>
                      <option value="Service Provider">Service Provider</option>
                      <option value="Contractor">Contractor</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Contact Person</Label>
                    <Input
                      value={formData.contactPerson}
                      onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                      placeholder="Primary contact name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="vendor@example.com"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Phone number"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Mobile</Label>
                    <Input
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder="Mobile number"
                    />
                  </FormGroup>
                </FormGrid>

                <SectionTitle>Address</SectionTitle>
                <FormGrid>
                  <FormGroup $full>
                    <Label>Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Street address"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="City"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Country</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="Country"
                    />
                  </FormGroup>
                </FormGrid>

                <SectionTitle>Tax Information</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>{registrationLabel} ({registrationLabelFull})</Label>
                    <Input
                      value={formData.ntn}
                      onChange={(e) => setFormData({ ...formData, ntn: e.target.value })}
                      placeholder={`${registrationLabel} registration number`}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>{taxLabel} Number</Label>
                    <Input
                      value={formData.gstNo}
                      onChange={(e) => setFormData({ ...formData, gstNo: e.target.value })}
                      placeholder={`${taxLabel} registration number`}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>WHT Applicable</Label>
                    <CheckboxWrapper>
                      <input
                        type="checkbox"
                        checked={formData.whtApplicable}
                        onChange={(e) => setFormData({ ...formData, whtApplicable: e.target.checked })}
                      />
                      <span>Deduct Withholding Tax on payments</span>
                    </CheckboxWrapper>
                  </FormGroup>
                  {formData.whtApplicable && (
                    <FormGroup>
                      <Label>WHT Rate (%)</Label>
                      <Select
                        value={formData.whtRate}
                        onChange={(e) => setFormData({ ...formData, whtRate: parseFloat(e.target.value) })}
                      >
                        <option value={4.5}>4.5% (Supplies)</option>
                        <option value={8}>8% (Services)</option>
                        <option value={10}>10% (Contracts)</option>
                      </Select>
                    </FormGroup>
                  )}
                </FormGrid>

                <SectionTitle>Bank Details</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Bank Name</Label>
                    <Input
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      placeholder="e.g., HBL, MCB, UBL"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Account Number</Label>
                    <Input
                      value={formData.bankAccountNo}
                      onChange={(e) => setFormData({ ...formData, bankAccountNo: e.target.value })}
                      placeholder="Bank account number"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Branch</Label>
                    <Input
                      value={formData.bankBranch}
                      onChange={(e) => setFormData({ ...formData, bankBranch: e.target.value })}
                      placeholder="Branch name"
                    />
                  </FormGroup>
                </FormGrid>

                <SectionTitle>Payment Terms & Status</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Payment Terms (Days)</Label>
                    <Input
                      type="number"
                      value={formData.paymentTerms}
                      onChange={(e) => setFormData({ ...formData, paymentTerms: parseInt(e.target.value) || 0 })}
                      placeholder="30"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Status</Label>
                    <Select
                      value={formData.isActive ? 'active' : 'inactive'}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <FormGroup $full>
                  <Label>Notes</Label>
                  <TextArea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Internal notes about this vendor..."
                    rows={3}
                  />
                </FormGroup>
                </div>

                {vendorPricingOn && (
                  <div style={{ display: modalTab === 'pricing' ? 'block' : 'none' }} data-vp-panel>
                    <VpHelp>
                      This vendor's own prices. When this vendor is picked on a Purchase Order, Goods Receipt,
                      Purchase Invoice or Non-Tax Purchase Invoice, the rate of a listed item is filled from here
                      (returns keep the invoice price). Items not listed keep their normal purchase price.
                      Prices are per base unit (a line in a bigger unit, like a box, is worked out from it).
                    </VpHelp>
                    {pricingRows.length > 5 && (
                      <VpSearch
                        placeholder="Search listed items..."
                        value={pricingSearch}
                        onChange={(e) => setPricingSearch(e.target.value)}
                      />
                    )}
                    <VpTable>
                      <thead>
                        <tr>
                          <VpTh style={{ width: '50%' }}>Item</VpTh>
                          <VpTh style={{ width: '18%', textAlign: 'right' }}>Normal Price</VpTh>
                          <VpTh style={{ width: '22%', textAlign: 'right' }}>Vendor Price (per base unit)</VpTh>
                          <VpTh style={{ width: '10%' }}></VpTh>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingRows.length === 0 && (
                          <tr><VpTd colSpan={4} style={{ textAlign: 'center', color: colors.text.secondary, padding: 20 }}>No items yet. Click "Add Item" to start.</VpTd></tr>
                        )}
                        {pricingRows.map((row, idx) => {
                          const q = pricingSearch.trim().toLowerCase();
                          if (q && row.itemId && !`${row.itemCode} ${row.itemName}`.toLowerCase().includes(q)) return null;
                          const item = pricingItems.find(i => i.id === row.itemId);
                          return (
                            <tr key={row.key}>
                              <VpTd>
                                <ItemSelect
                                  items={pricingItems.filter(it => it.id === row.itemId || !pricingRows.some(r => r.itemId === it.id))}
                                  selectedItemId={row.itemId}
                                  disabled={!canEdit}
                                  onChange={(itemId) => {
                                    const picked = pricingItems.find(i => i.id === itemId);
                                    setPricingRows(prev => prev.map((r, i) => (i !== idx ? r : {
                                      ...r,
                                      itemId: itemId || '',
                                      itemCode: picked?.itemCode || '',
                                      itemName: picked?.itemName || '',
                                      price: r.price || Number(picked?.purchasePrice) || '',
                                    })));
                                  }}
                                  placeholder="Pick item"
                                />
                              </VpTd>
                              <VpTd style={{ textAlign: 'right', color: colors.text.secondary }}>
                                {item && Number(item.purchasePrice) ? `${Number(item.purchasePrice).toLocaleString('en-PK', { maximumFractionDigits: 2 })}${getBaseUnit(item) ? ` / ${getBaseUnit(item)}` : ''}` : '—'}
                              </VpTd>
                              <VpTd>
                                <VpPrice
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={row.price}
                                  disabled={!canEdit}
                                  aria-label={`Vendor price ${row.itemCode || idx + 1}`}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setPricingRows(prev => prev.map((r, i) => (i !== idx ? r : { ...r, price: v === '' ? '' : Number(v) })));
                                  }}
                                />
                              </VpTd>
                              <VpTd style={{ textAlign: 'center' }}>
                                {canEdit && (
                                  <VpRemove type="button" title="Remove" onClick={() => setPricingRows(prev => prev.filter((_, i) => i !== idx))}>
                                    <FiTrash2 />
                                  </VpRemove>
                                )}
                              </VpTd>
                            </tr>
                          );
                        })}
                      </tbody>
                    </VpTable>
                    {canEdit && (
                      <VpAdd
                        type="button"
                        onClick={() => setPricingRows(prev => [...prev, { key: `new_${Date.now()}_${prev.length}`, itemId: '', itemCode: '', itemName: '', price: '' }])}
                      >
                        <FiPlus /> Add Item
                      </VpAdd>
                    )}
                  </div>
                )}

                <ModalActions>
                  <CancelButton type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </CancelButton>
                  {canEdit && (
                    <SaveButton type="submit" disabled={saving}>
                      <FiSave />
                      {saving ? 'Saving...' : (editingVendor ? 'Update Vendor' : 'Create Vendor')}
                    </SaveButton>
                  )}
                </ModalActions>
              </form>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components

const ImportBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  background: white; color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}50;
  border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer;
  &:hover { background: ${colors.primary.main}10; }
`;

const ActionDropdown = styled.div`
  position: fixed;
  min-width: 200px;
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  z-index: 9999;
  padding: 4px;
  overflow: hidden;
`;
const ActionItem = styled.button`
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none; border: none; text-align: left;
  font-size: 13px; cursor: pointer;
  color: ${p => p.$danger ? colors.error.main : colors.text.primary};
  border-radius: 6px;
  &:hover { background: ${p => p.$danger ? `${colors.error.main}10` : colors.neutral[50]}; }
  svg { flex-shrink: 0; opacity: 0.75; }
`;
const ActionDivider = styled.div`
  height: 1px; background: ${colors.neutral[200]}; margin: 4px 0;
`;
const ImportOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 1100;
`;
const ImportModalBox = styled.div`
  background: white; border-radius: 10px; width: 92%; max-width: 520px;
  max-height: 80vh; overflow-y: auto;
`;
const ImportHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 18px; border-bottom: 1px solid ${colors.neutral[200]};
  h3 { margin: 0; font-size: 15px; }
`;
const ImportBody = styled.div`padding: 18px;`;
const ImportFooter = styled.div`
  display: flex; justify-content: flex-end;
  padding: 12px 18px; border-top: 1px solid ${colors.neutral[200]};
`;
const ProgressBarOuter = styled.div`
  width: 100%; height: 8px; background: ${colors.neutral[200]}; border-radius: 4px; overflow: hidden;
`;
const ProgressBarFill = styled.div`
  height: 100%; background: ${colors.primary.main}; transition: width 0.2s;
`;
const ResultRow = styled.div`
  padding: 8px 12px; margin-bottom: 6px; border-radius: 6px;
  background: ${p => p.$color}15; color: ${p => p.$color};
  font-size: 13px;
`;
const ErrorList = styled.div`
  margin-top: 12px; padding: 10px 12px;
  background: ${colors.neutral[50]}; border-radius: 6px;
  max-height: 220px; overflow-y: auto;
`;
const ErrorItem = styled.div`
  font-size: 12px; color: ${colors.text.secondary};
  padding: 4px 0; border-bottom: 1px dashed ${colors.neutral[200]};
  &:last-child { border-bottom: none; }
`;

const SearchBar = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.border.light};
`;

const SearchInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  max-width: 400px;

  svg {
    position: absolute;
    left: 12px;
    color: ${colors.text.tertiary};
  }

  input {
    width: 100%;
    padding: 10px 10px 10px 40px;
    border: 1px solid ${colors.border.light};
    border-radius: ${borderRadius.md};
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: ${colors.background.paper};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  border-bottom: 1px solid ${colors.border.light};
`;

const Tr = styled.tr`
  &:hover {
    background: ${colors.background.paper};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.border.light};
`;

const VendorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const VendorName = styled.span`
  font-weight: 500;
`;

const ContactPerson = styled.span`
  font-size: 12px;
  color: ${colors.text.tertiary};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$type === 'Service Provider' ? colors.secondary.main + '20' : colors.primary.main + '20'};
  color: ${props => props.$type === 'Service Provider' ? colors.secondary.main : colors.primary.main};
`;

const WhtBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  font-size: 11px;
  font-weight: 600;
  background: ${colors.status.warning}20;
  color: ${colors.status.warning};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${borderRadius.xl};
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? colors.status.success + '20' : colors.neutral[200]};
  color: ${props => props.$active ? colors.status.success : colors.text.tertiary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${props => props.$danger ? colors.status.error + '10' : colors.background.paper};
  color: ${props => props.$danger ? colors.status.error : colors.text.secondary};
  border: none;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$danger ? colors.status.error + '20' : colors.primary.main + '10'};
    color: ${props => props.$danger ? colors.status.error : colors.primary.main};
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: ${colors.text.secondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: ${colors.text.tertiary};

  svg {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 16px;
    margin-bottom: 8px;
  }

  small {
    font-size: 13px;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.border.light};
`;

const ModalTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};

  svg {
    color: ${colors.primary.main};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: ${colors.text.tertiary};
  cursor: pointer;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.paper};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

// Vendor-wise Items & Pricing tab (module purchases_vendor_pricing).
const VpTabStrip = styled.div`
  display: flex; gap: 4px; border-bottom: 1px solid ${colors.border.light}; margin-bottom: 16px;
`;
const VpTabBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: transparent; border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? colors.primary.main : 'transparent')};
  color: ${({ $active }) => ($active ? colors.primary.main : colors.text.secondary)};
  font-size: 13px; font-weight: ${({ $active }) => ($active ? 600 : 500)}; cursor: pointer; margin-bottom: -1px;
  &:hover { color: ${colors.primary.main}; }
`;
const VpCount = styled.span`
  font-size: 11px; font-weight: 600; padding: 1px 6px; border-radius: 10px;
  background: ${colors.primary.main}15; color: ${colors.primary.main};
`;
const VpHelp = styled.div`
  padding: 10px 12px; background: ${colors.primary.main}08; border-left: 3px solid ${colors.primary.main};
  border-radius: 4px; font-size: 12px; color: ${colors.text.secondary}; line-height: 1.5; margin-bottom: 14px;
`;
const VpSearch = styled.input`
  width: 100%; max-width: 320px; padding: 8px 10px; margin-bottom: 10px; font-size: 13px;
  border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.md};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const VpTable = styled.table`
  width: 100%; border-collapse: collapse; border: 1px solid ${colors.border.light}; border-radius: 4px;
`;
const VpTh = styled.th`
  padding: 8px 10px; text-align: left; font-size: 12px; font-weight: 600; color: ${colors.text.secondary};
  background: ${colors.background.paper}; border-bottom: 1px solid ${colors.border.light};
`;
const VpTd = styled.td`
  padding: 8px 10px; font-size: 13px; border-bottom: 1px solid ${colors.border.light}; vertical-align: middle;
`;
const VpPrice = styled.input`
  width: 100%; min-width: 0; padding: 7px 8px; text-align: right; font-size: 13px;
  border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const VpRemove = styled.button`
  display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px;
  background: transparent; border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm};
  color: ${colors.error.main}; cursor: pointer;
`;
const VpAdd = styled.button`
  display: inline-flex; align-items: center; gap: 6px; margin-top: 12px; padding: 8px 14px; font-size: 13px;
  background: white; border: 1px dashed ${colors.primary.main}; color: ${colors.primary.main};
  border-radius: ${borderRadius.md}; cursor: pointer;
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.border.light};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: ${props => props.$full ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}20;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: ${colors.text.secondary};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.background.paper};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default Vendors;
